import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import Swal from 'sweetalert2';
import {
  FileText,
  Image,
  X
} from 'lucide-react';

function WinnerDraw({ campaignId, campaign, setSuccessMessage, setShowSuccess }) {
  const [drawnWinners, setDrawnWinners] = useState([]);
  const [previousWinners, setPreviousWinners] = useState([]);
  const [drawingWinners, setDrawingWinners] = useState(false);
  const [hasExistingWinners, setHasExistingWinners] = useState(false);
  const [selectedDrawDateId, setSelectedDrawDateId] = useState('');
  const [drawDates, setDrawDates] = useState([]);
  const [showDrawDateManager, setShowDrawDateManager] = useState(false);
  const [newDrawDate, setNewDrawDate] = useState({ date: '', label: '', maxDraw: 3 });
  const [editingDrawDate, setEditingDrawDate] = useState(null);
  const [viewingEntry, setViewingEntry] = useState(null);
  const [entryDetails, setEntryDetails] = useState(null);
  const [loadingEntry, setLoadingEntry] = useState(false);

  useEffect(() => {
    if (campaignId) {
      fetchPreviousWinners();
      fetchDrawDates();
    }
  }, [campaignId]);

  const fetchPreviousWinners = async () => {
    try {
      const response = await api.get(`/admin/campaigns/${campaignId}/previous-winners`);
      const winners = response.data.winners || [];
      setPreviousWinners(winners);
      const hasWinners = winners.length > 0 && winners.some(draw => draw.winners && draw.winners.length > 0);
      setHasExistingWinners(hasWinners);
    } catch (error) {
      console.error('Error fetching previous winners:', error);
    }
  };

  const fetchDrawDates = async () => {
    try {
      const response = await api.get(`/admin/campaigns/${campaignId}/draw-dates`);
      const dates = response.data.drawDates || [];
      setDrawDates(dates);
      // Set default to first available draw date
      if (dates.length > 0 && !selectedDrawDateId) {
        setSelectedDrawDateId(dates[0].id);
      }
    } catch (error) {
      console.error('Error fetching draw dates:', error);
      await Swal.fire('Error', 'Failed to load draw dates', 'error');
    }
  };

  const fetchEntryDetails = async (entryId, winnerData) => {
    setLoadingEntry(true);
    try {
      const response = await api.get(`/admin/campaigns/${campaignId}/entries`);
      const entries = response.data.entries || [];
      const entry = entries.find(e => e.id === parseInt(entryId));
      
      if (entry) {
        setEntryDetails(entry);
      } else if (winnerData) {
        // If not found in current results, use the winner data we have
        // Create a mock entry from the winner data
        const mockEntry = {
          id: winnerData.entry_id || winnerData.id,
          entry_number: winnerData.entry_number,
          created_at: winnerData.created_at || winnerData.won_at || new Date().toISOString(),
          status: 'approved', // Winners are always approved
          entry_data: {
            name: winnerData.name,
            email: winnerData.email,
            phone: winnerData.phone
          },
          dynamic_form_data: winnerData.dynamic_form_data || {},
          user_name: winnerData.name,
          user_email: winnerData.email,
          // Include any receipt data if available
          receipt_url: winnerData.receipt_url,
          receipt_status: winnerData.receipt_status,
          receipt_confidence: winnerData.receipt_confidence,
          receipt_metadata: winnerData.receipt_metadata,
          instant_win_won: winnerData.instant_win_won,
          instant_win_prize: winnerData.instant_win_prize,
          instant_win_value: winnerData.instant_win_value
        };
        setEntryDetails(mockEntry);
      } else {
        throw new Error('Entry not found');
      }
    } catch (error) {
      console.error('Error fetching entry details:', error);
      if (winnerData) {
        // Use winner data as fallback
        const mockEntry = {
          id: winnerData.entry_id || winnerData.id,
          entry_number: winnerData.entry_number,
          created_at: winnerData.created_at || winnerData.won_at || new Date().toISOString(),
          status: 'approved',
          entry_data: {
            name: winnerData.name,
            email: winnerData.email,
            phone: winnerData.phone
          },
          dynamic_form_data: winnerData.dynamic_form_data || {},
          user_name: winnerData.name,
          user_email: winnerData.email,
          receipt_url: winnerData.receipt_url,
          receipt_status: winnerData.receipt_status,
          receipt_confidence: winnerData.receipt_confidence,
          receipt_metadata: winnerData.receipt_metadata,
          instant_win_won: winnerData.instant_win_won,
          instant_win_prize: winnerData.instant_win_prize,
          instant_win_value: winnerData.instant_win_value
        };
        setEntryDetails(mockEntry);
      } else {
        setEntryDetails(null);
      }
    } finally {
      setLoadingEntry(false);
    }
  };

  const handleViewEntry = (winner) => {
    setViewingEntry(winner);
    fetchEntryDetails(winner.entry_id || winner.id, winner);
  };

  const handleDrawWinners = async () => {
    if (!selectedDrawDateId) {
      await Swal.fire('Error', 'Please select a draw date first', 'error');
      return;
    }

    // Get the selected draw date configuration
    const selectedDrawDateConfig = drawDates.find(dd => dd.id == selectedDrawDateId);
    if (!selectedDrawDateConfig) {
      await Swal.fire('Error', 'Selected draw date configuration not found', 'error');
      return;
    }

    const numberOfWinnersToDraw = selectedDrawDateConfig.maxDraw;

    setDrawingWinners(true);
    try {
      const response = await api.post(`/admin/campaigns/${campaignId}/draw-winners`, {
        numberOfWinners: numberOfWinnersToDraw,
        drawDateId: selectedDrawDateId
      });
      setDrawnWinners(response.data.winners);
    } catch (error) {
      console.error('Error drawing winners:', error);
      const errorMessage = error.response?.data?.error || 'Failed to draw winners';
      await Swal.fire('Error', errorMessage, 'error');
    } finally {
      setDrawingWinners(false);
    }
  };


  const handleCreateDrawDate = async () => {
    if (!newDrawDate.date || !newDrawDate.label) {
      await Swal.fire('Error', 'Please fill in all required fields', 'error');
      return;
    }

    try {
      const response = await api.post(`/admin/campaigns/${campaignId}/draw-dates`, {
        drawDate: newDrawDate.date,
        drawLabel: newDrawDate.label,
        maxDraw: newDrawDate.maxDraw
      });

      if (response.data.success) {
        await fetchDrawDates();
        setNewDrawDate({ date: '', label: '', maxDraw: 3 });
        setShowDrawDateManager(false);
        await Swal.fire('Success', 'Draw date created successfully!', 'success');
      }
    } catch (error) {
      console.error('Error creating draw date:', error);
      await Swal.fire('Error', error.response?.data?.error || 'Failed to create draw date', 'error');
    }
  };

  const handleUpdateDrawDate = async () => {
    if (!editingDrawDate.date || !editingDrawDate.label) {
      await Swal.fire('Error', 'Please fill in all required fields', 'error');
      return;
    }

    try {
      const response = await api.put(`/admin/campaigns/${campaignId}/draw-dates/${editingDrawDate.id}`, {
        drawDate: editingDrawDate.date,
        drawLabel: editingDrawDate.label,
        maxDraw: editingDrawDate.maxDraw,
        isActive: editingDrawDate.isActive
      });

      if (response.data.success) {
        await fetchDrawDates();
        setEditingDrawDate(null);
        await Swal.fire('Success', 'Draw date updated successfully!', 'success');
      }
    } catch (error) {
      console.error('Error updating draw date:', error);
      await Swal.fire('Error', error.response?.data?.error || 'Failed to update draw date', 'error');
    }
  };

  const handleDeleteDrawDate = async (drawDateId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will permanently delete the draw date. This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await api.delete(`/admin/campaigns/${campaignId}/draw-dates/${drawDateId}`);
        
        if (response.data.success) {
          await fetchDrawDates();
          await Swal.fire('Success', 'Draw date deleted successfully!', 'success');
        }
      } catch (error) {
        console.error('Error deleting draw date:', error);
        await Swal.fire('Error', error.response?.data?.error || 'Failed to delete draw date', 'error');
      }
    }
  };

  const handleRedrawWinner = async (winnerToRemove) => {
    // If it's from the drawn winners (not yet saved), just remove from state
    if (drawnWinners.some(w => w.id === winnerToRemove.id)) {
      setDrawnWinners(prev => prev.filter(w => w.id !== winnerToRemove.id));
    } else {
      // If it's from previous winners (already saved), remove from database
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'This will remove this winner from the database. This action cannot be undone!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, remove winner!',
        cancelButtonText: 'Cancel'
      });

      if (result.isConfirmed) {
        try {
          const winnerRecordId = winnerToRemove.winner_id || winnerToRemove.winnerId;
          if (!winnerRecordId) {
            console.error('Cannot remove winner: missing winner_id', winnerToRemove);
            await Swal.fire('Error', 'Unable to identify the selected winner record.', 'error');
            return;
          }

          await api.delete(`/admin/campaigns/${campaignId}/winners/${winnerRecordId}`);
          await fetchPreviousWinners();
          await fetchDrawDates();
          setSuccessMessage('Winner removed successfully!');
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 3000);
        } catch (error) {
          console.error('Error removing winner:', error);
          await Swal.fire('Error', error.response?.data?.error || 'Failed to remove winner', 'error');
        }
      }
    }
  };

  const handleRedraw = () => {
    setDrawnWinners([]);
  };

  // Check if all draws are finished (all draw dates have winners)
  const areAllDrawsFinished = () => {
    return drawDates.length > 0 && drawDates.every(drawDate => drawDate.winnersCount > 0);
  };

  const handleSelectWinner = async (winner) => {
    try {
      console.log('Selecting winner:', winner);
      console.log('Selected draw date ID:', selectedDrawDateId);
      
      const response = await api.post(`/admin/campaigns/${campaignId}/save-selected-winner`, {
        entryId: winner.id,
        position: winner.position || 1,
        drawDateId: selectedDrawDateId
      });

      console.log('Response:', response.data);

      if (response.data.success) {
        // Remove the selected winner from drawn winners
        setDrawnWinners(prev => prev.filter(w => w.id !== winner.id));
        // Refresh previous winners and draw dates
        await fetchPreviousWinners();
        await fetchDrawDates();
        setSuccessMessage('Winner saved successfully!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Error saving selected winner:', error);
      await Swal.fire('Error', error.response?.data?.error || 'Failed to save winner', 'error');
    }
  };

  const handleResetWinners = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      html: 'This will remove all existing winners for this campaign.<br><br><strong>This action cannot be undone!</strong>',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, reset winners!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.post(`/admin/campaigns/${campaignId}/reset-winners`);
        await fetchPreviousWinners();
        setSuccessMessage('Winners have been reset successfully!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } catch (error) {
        console.error('Error resetting winners:', error);
        await Swal.fire('Error', error.response?.data?.error || 'Failed to reset winners', 'error');
      }
    }
  };

  const allDrawsFinished = areAllDrawsFinished();

  return (
    <div className="p-8">
      <h3 className="text-xl font-bold text-gray-900 mb-6">Winner Draw</h3>
      
      {/* Campaign Info */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-600">
          Campaign: <span className="font-medium">{campaign?.name}</span>
        </p>
        <p className="text-sm text-gray-600 mt-1">
          Total Approved Entries: <span className="font-medium">{campaign?.totalApprovedEntries || 0}</span>
        </p>
        
        {/* Draw Configuration - Always show to allow multiple draws */}
        {!allDrawsFinished && (
          <div className="mt-3 space-y-4">
            {/* Draw Date Selection */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Select Draw Date
                </label>
                <button
                  onClick={() => setShowDrawDateManager(true)}
                  className="text-xs text-teal-600 hover:text-teal-700 font-medium"
                >
                  Manage Draw Dates
                </button>
              </div>
              <select
                value={selectedDrawDateId}
                onChange={(e) => {
                  setSelectedDrawDateId(e.target.value);
                  // Clear drawn winners when changing draw date
                  setDrawnWinners([]);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">Select a draw date...</option>
                {drawDates.map((drawDate) => (
                  <option key={drawDate.id} value={drawDate.id}>
                    {drawDate.label} {drawDate.winnersCount > 0 && `(${drawDate.winnersCount} winners already drawn)`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
        
      </div>

      {/* Draw Winners Button */}
      {!allDrawsFinished && (
        drawnWinners.length === 0 ? (
          <div className="mb-8">
            <div className="flex gap-4">
              <button
                onClick={handleDrawWinners}
                disabled={drawingWinners || !selectedDrawDateId}
                className="flex-1 px-6 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-600 disabled:opacity-50 font-medium"
              >
                {drawingWinners ? 'Drawing Winners...' : 'Draw Winners'}
              </button>
              {hasExistingWinners && (
                <button
                  onClick={handleResetWinners}
                  className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium"
                >
                  Reset All Winners
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Drawn Winners Display */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Selected Winners ({drawnWinners.length})</h4>
              <div className={`grid gap-4 ${drawnWinners.length <= 3 ? 'md:grid-cols-3' : drawnWinners.length <= 4 ? 'md:grid-cols-4' : 'md:grid-cols-3 lg:grid-cols-4'}`}>
                {drawnWinners.map((winner, index) => (
                  <div key={winner.id} className="bg-white rounded-lg shadow-md p-4">
                    <div className="mb-3">
                      <h5 className="text-lg font-semibold text-gray-900">{winner.name}</h5>
                      <p className="text-gray-600 text-sm">{winner.email}</p>
                      {winner.phone && <p className="text-gray-500 text-sm">{winner.phone}</p>}
                      <p className="text-xs text-gray-400 mt-2">Entry #{winner.submission_order || winner.entry_number}</p>
                    </div>
                    <div className="space-y-2">
                      <button
                        onClick={() => handleViewEntry(winner)}
                        className="w-full flex items-center justify-center px-3 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors text-sm"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        View Entry Details
                      </button>
                      <button
                        onClick={() => handleSelectWinner(winner)}
                        className="w-full flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Select Winner
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Redraw Button */}
              <div className="mt-6 flex justify-center">
                <button
                  onClick={handleRedraw}
                  className="px-6 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 font-medium"
                >
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Redraw Winners
                </button>
              </div>
            </div>
          </>
        )
      )}
      
      {allDrawsFinished && hasExistingWinners && (
        <div className="mb-8">
          <div className="p-6 bg-teal-50 border border-teal-200 rounded-xl">
            <h4 className="text-lg font-semibold text-teal-900 mb-2">All draw dates are complete</h4>
            <p className="text-sm text-teal-700">
              Every scheduled draw already has assigned winners. Reset all winners to enable new draws.
            </p>
            <div className="mt-4">
              <button
                onClick={handleResetWinners}
                className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 font-medium"
              >
                Reset All Winners
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Draw Winners History */}
      {previousWinners.length > 0 && (
        <div className="mt-8 border-t pt-8">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Draw Winners</h4>
          
          {/* Group winners by draw date */}
          {previousWinners.map((draw, drawIndex) => (
            <div key={drawIndex} className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-md font-semibold text-gray-800">
                  {draw.draw_label || (draw.draw_date ? new Date(draw.draw_date).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  }) : draw.draw_label)}
                </h5>
                <span className="text-sm text-gray-500">
                  {draw.winners.length} winner{draw.winners.length !== 1 ? 's' : ''}
                </span>
              </div>
              
              <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Entry #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Draw Date</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {draw.winners.sort((a, b) => a.position - b.position).map((winner, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          #{winner.submission_order || winner.entry_number || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {winner.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {winner.email || 'No email'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {winner.phone || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {winner.draw_label}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <div className="flex space-x-2 justify-end">
                            <button
                              onClick={() => handleViewEntry({ 
                                ...winner, 
                                entry_id: winner.entry_id || winner.id,
                                id: winner.entry_id || winner.id,
                                winner_id: winner.winner_id || winner.winnerId,
                                won_at: winner.drawn_at || winner.won_at
                              })}
                              className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              View Entry
                            </button>
                            <button
                              onClick={() => handleRedrawWinner({ 
                                ...winner, 
                                entry_id: winner.entry_id || winner.id,
                                id: winner.entry_id || winner.id,
                                winner_id: winner.winner_id || winner.winnerId,
                                won_at: winner.drawn_at || winner.won_at
                              })}
                              className="inline-flex items-center px-3 py-1.5 bg-orange-50 text-orange-700 rounded-md hover:bg-orange-100 transition-colors"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Redraw
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Entry Details Modal */}
      {viewingEntry && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-700 to-teal-800 px-6 py-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">Entry Details - {viewingEntry.name}</h3>
                <button
                  onClick={() => {
                    setViewingEntry(null);
                    setEntryDetails(null);
                  }}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              {loadingEntry ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading entry details...</p>
                </div>
              ) : entryDetails ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
                  {/* Left Column - Entry Details */}
                  <div className="space-y-6">
                    {/* Entry Number Header */}
                    <div className="pb-4 border-b border-gray-200">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                        </svg>
                        <span className="text-sm font-medium text-gray-600">Entry Number</span>
                      </div>
                      <p className="text-lg font-mono font-semibold text-gray-900 mt-1">#{entryDetails.entry_number || viewingEntry.entry_number || 'N/A'}</p>
                      <p className="text-sm text-gray-500 mt-1">Submitted on {new Date(entryDetails.created_at).toLocaleString()}</p>
                    </div>

                    {/* Instant Win Prize */}
                    {entryDetails.instant_win_won ? (
                      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-4">
                        <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                          <svg className="w-6 h-6 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                          </svg>
                          Instant Win Prize Winner!
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-600">Prize</p>
                            <p className="font-semibold text-gray-900">{entryDetails.instant_win_prize}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Value</p>
                            <p className="font-semibold text-gray-900">${entryDetails.instant_win_value}</p>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {/* User Information */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <svg className="w-5 h-5 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        User Information
                      </h4>
                      <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-1 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Full Name</p>
                          <p className="font-medium text-gray-900">{entryDetails.entry_data?.name || entryDetails.user_name || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Email Address</p>
                          <p className="font-medium text-gray-900">{entryDetails.entry_data?.email || entryDetails.user_email || '-'}</p>
                        </div>
                        {entryDetails.entry_data?.phone && (
                          <div>
                            <p className="text-sm text-gray-600">Phone Number</p>
                            <p className="font-medium text-gray-900">{entryDetails.entry_data.phone}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Dynamic Form Fields */}
                    {entryDetails.dynamic_form_data && Object.entries(entryDetails.dynamic_form_data).filter(([key, value]) => value && value !== '').length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                          <svg className="w-5 h-5 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Form Responses
                        </h4>
                        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                          {Object.entries(entryDetails.dynamic_form_data)
                            .filter(([key, value]) => value && value !== '')
                            .map(([key, value]) => (
                            <div key={key} className="flex justify-between items-start">
                              <span className="text-sm text-gray-600">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                              <span className="text-sm font-medium text-gray-900 ml-4 text-right">
                                {Array.isArray(value) ? value.join(', ') : value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Winner Position */}
                    <div>
                      <h4 className="text-base font-semibold text-gray-900 mb-3">Winner Details</h4>
                      <div className="bg-teal-50 rounded-lg p-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-teal-700 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                            {viewingEntry.position || '#'}
                          </div>
                          <div>
                            <p className="font-medium text-teal-900">
                              {viewingEntry.position === 1 ? '1st Place Winner' :
                               viewingEntry.position === 2 ? '2nd Place Winner' :
                               viewingEntry.position === 3 ? '3rd Place Winner' :
                               `${viewingEntry.position}th Place Winner`}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Receipt */}
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <svg className="w-5 h-5 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        Receipt Information
                      </h4>

                      {entryDetails.receipt_url ? (
                        <div className="space-y-4">
                          {/* Receipt Status */}
                          <div className="bg-gray-50 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <p className="text-sm text-gray-600 mb-1">Receipt Status</p>
                                <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${
                                  entryDetails.receipt_status === 'valid' ? 'bg-green-100 text-green-800' :
                                  entryDetails.receipt_status === 'invalid' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {entryDetails.receipt_status || 'Pending Review'}
                                </span>
                              </div>
                              <a 
                                href={entryDetails.receipt_url.startsWith('http') ? entryDetails.receipt_url : `${process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || ''}${entryDetails.receipt_url}`}
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View Receipt
                              </a>
                            </div>
                            
                            {entryDetails.receipt_confidence && (
                              <div>
                                <p className="text-sm text-gray-600">Match Confidence</p>
                                <div className="flex items-center mt-1">
                                  <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                                    <div 
                                      className={`h-2 rounded-full ${
                                        entryDetails.receipt_confidence >= 0.7 ? 'bg-green-500' : 'bg-yellow-500'
                                      }`}
                                      style={{ width: `${entryDetails.receipt_confidence * 100}%` }}
                                    />
                                  </div>
                                  <span className={`text-sm font-medium ${
                                    entryDetails.receipt_confidence >= 0.7 ? 'text-green-600' : 'text-yellow-600'
                                  }`}>
                                    {Math.round(entryDetails.receipt_confidence * 100)}%
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Receipt Preview */}
                          <div className="bg-gray-50 rounded-xl p-4">
                            <div className="text-center">
                              <img
                                src={entryDetails.receipt_url.startsWith('http') ? entryDetails.receipt_url : `${process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || ''}${entryDetails.receipt_url}`}
                                alt="Receipt"
                                className="max-w-full max-h-96 mx-auto rounded-lg shadow-md"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <div className="hidden">
                                <Image className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                                <p className="text-gray-600">Receipt image could not be loaded</p>
                              </div>
                            </div>
                          </div>

                          {/* Receipt Metadata */}
                          {entryDetails.receipt_metadata && (
                            <div className="space-y-4">
                              {/* Store Information */}
                              {entryDetails.receipt_metadata.store && (
                                <div className="bg-blue-50 rounded-xl p-4">
                                  <p className="text-sm font-semibold text-gray-700 mb-2">Store Information</p>
                                  {entryDetails.receipt_metadata.store.name && (
                                    <p className="text-sm text-gray-900"><span className="text-gray-600">Name:</span> {entryDetails.receipt_metadata.store.name}</p>
                                  )}
                                  {entryDetails.receipt_metadata.store.address && (
                                    <p className="text-sm text-gray-900"><span className="text-gray-600">Address:</span> {entryDetails.receipt_metadata.store.address}</p>
                                  )}
                                </div>
                              )}

                              {/* Transaction Details */}
                              {(entryDetails.receipt_metadata.date || entryDetails.receipt_metadata.transactionNumber) && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {entryDetails.receipt_metadata.date && (
                                    <div className="bg-purple-50 rounded-xl p-4">
                                      <p className="text-sm text-gray-600">Purchase Date</p>
                                      <p className="font-medium text-gray-900">
                                        {new Date(entryDetails.receipt_metadata.date).toLocaleDateString()}
                                      </p>
                                    </div>
                                  )}
                                  {entryDetails.receipt_metadata.transactionNumber && (
                                    <div className="bg-purple-50 rounded-xl p-4">
                                      <p className="text-sm text-gray-600">Transaction #</p>
                                      <p className="font-medium text-gray-900">{entryDetails.receipt_metadata.transactionNumber}</p>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Price Information */}
                              {entryDetails.receipt_metadata.prices && (
                                <div className="bg-green-50 rounded-xl p-4">
                                  <p className="text-sm font-semibold text-gray-700 mb-3">Purchase Summary</p>
                                  
                                  {entryDetails.receipt_metadata.prices.items && entryDetails.receipt_metadata.prices.items.length > 0 && (
                                    <div className="space-y-2 mb-3 pb-3 border-b border-green-200">
                                      {entryDetails.receipt_metadata.prices.items.map((item, index) => (
                                        <div key={index} className="flex justify-between text-sm">
                                          <span className="text-gray-700">{item.product}</span>
                                          <span className="font-medium text-gray-900">${item.price.toFixed(2)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  <div className="space-y-1">
                                    {entryDetails.receipt_metadata.prices.subtotal && (
                                      <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Subtotal</span>
                                        <span className="text-gray-900">${entryDetails.receipt_metadata.prices.subtotal.toFixed(2)}</span>
                                      </div>
                                    )}
                                    {entryDetails.receipt_metadata.prices.total && (
                                      <div className="flex justify-between text-base font-semibold pt-2 border-t border-green-200">
                                        <span className="text-gray-700">Total</span>
                                        <span className="text-gray-900">${entryDetails.receipt_metadata.prices.total.toFixed(2)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-xl p-8 text-center">
                          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-600">No receipt uploaded</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">No entry details available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Draw Date Management Modal */}
      {showDrawDateManager && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-700 to-teal-800 px-6 py-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">Manage Draw Dates</h3>
                <button
                  onClick={() => {
                    setShowDrawDateManager(false);
                    setEditingDrawDate(null);
                    setNewDrawDate({ date: '', label: '', maxDraw: 3 });
                  }}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
              {/* Existing Draw Dates */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Current Draw Dates</h4>
                <div className="space-y-4">
                  {drawDates.map((drawDate) => (
                    <div key={drawDate.id} className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-900">{drawDate.label}</h5>
                        <p className="text-sm text-gray-600">
                          Date: {new Date(drawDate.date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </p>
                        <p className="text-sm text-gray-600">
                          Max Draw: {drawDate.maxDraw} | 
                          Winners Drawn: {drawDate.winnersCount} | 
                          Status: {drawDate.isActive ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingDrawDate({ ...drawDate })}
                          className="px-3 py-1 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteDrawDate(drawDate.id)}
                          disabled={drawDate.winnersCount > 0}
                          className="px-3 py-1 bg-red-50 text-red-700 rounded-md hover:bg-red-100 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add New Draw Date */}
              <div className="border-t pt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                  {editingDrawDate ? 'Edit Draw Date' : 'Add New Draw Date'}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Draw Date *
                    </label>
                    <input
                      type="date"
                      value={editingDrawDate ? editingDrawDate.date : newDrawDate.date}
                      onChange={(e) => {
                        if (editingDrawDate) {
                          setEditingDrawDate({ ...editingDrawDate, date: e.target.value });
                        } else {
                          setNewDrawDate({ ...newDrawDate, date: e.target.value });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Draw Label *
                    </label>
                    <input
                      type="text"
                      value={editingDrawDate ? editingDrawDate.label : newDrawDate.label}
                      onChange={(e) => {
                        if (editingDrawDate) {
                          setEditingDrawDate({ ...editingDrawDate, label: e.target.value });
                        } else {
                          setNewDrawDate({ ...newDrawDate, label: e.target.value });
                        }
                      }}
                      placeholder="e.g., First Draw (June 4-9, 2025)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Draw
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={editingDrawDate ? editingDrawDate.maxDraw : newDrawDate.maxDraw}
                      onChange={(e) => {
                        if (editingDrawDate) {
                          setEditingDrawDate({ ...editingDrawDate, maxDraw: parseInt(e.target.value) || 3 });
                        } else {
                          setNewDrawDate({ ...newDrawDate, maxDraw: parseInt(e.target.value) || 3 });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                </div>
                
                {editingDrawDate && (
                  <div className="mt-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingDrawDate.isActive}
                        onChange={(e) => setEditingDrawDate({ ...editingDrawDate, isActive: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                )}

                <div className="mt-6 flex space-x-4">
                  {editingDrawDate ? (
                    <>
                      <button
                        onClick={handleUpdateDrawDate}
                        className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
                      >
                        Update Draw Date
                      </button>
                      <button
                        onClick={() => setEditingDrawDate(null)}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={handleCreateDrawDate}
                        className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
                      >
                        Add Draw Date
                      </button>
                      <button
                        onClick={() => setNewDrawDate({ date: '', label: '', maxWinners: 3 })}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                      >
                        Clear
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WinnerDraw;
